import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type {
  Block,
  Inline,
  Text as ContentfulText,
} from '@contentful/rich-text-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { ReactNode } from 'react';
import React, { useState } from 'react';

import { InsertDynamicaValuesIntoTextWrapper } from '@/components/Cms/FreeTextContent';
import { Flex } from '@/components/Flex';
import { List } from '@/components/List';
import type {
  ContentfulCardFragment,
  ContentfulHeroProductListingFragment,
} from '@/graphql/contentful/api';
import { theme } from '@/theme';

import {
  RENDER_TEXT_OPTION,
  renderMarkOption,
  renderTableRow,
  useTableData,
} from './CardRichTextShared';
import { ShowMore } from './CardRichTextShowMore';

import * as S from './CardRichTextRenderer.styles';

const DEFAULT_CONFIG = {
  CHARACTER_LIMIT: 150,
} as const;

const renderNodeOption = (backgroundColor?: string) => ({
  [BLOCKS.UL_LIST]: (node: Block | Inline, children: ReactNode) => (
    <List>{children}</List>
  ),
  [BLOCKS.OL_LIST]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledOl>{children}</S.StyledOl>
  ),
  [BLOCKS.LIST_ITEM]: (node: Block | Inline, children: ReactNode) => (
    <S.ListItem>{children}</S.ListItem>
  ),
  [BLOCKS.HEADING_1]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledH1>{children}</S.StyledH1>
  ),
  [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledH2>{children}</S.StyledH2>
  ),
  [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledH3>{children}</S.StyledH3>
  ),
  [BLOCKS.HEADING_4]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledH4>{children}</S.StyledH4>
  ),
  [BLOCKS.HEADING_5]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledH5>{children}</S.StyledH5>
  ),
  [BLOCKS.HEADING_6]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledH6>{children}</S.StyledH6>
  ),
  [BLOCKS.QUOTE]: (node: Block | Inline, children: ReactNode) => (
    <S.StyledQuote>{children}</S.StyledQuote>
  ),
  [BLOCKS.HR]: () => <hr />,
  [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
    const { uri } = node.data;
    const isExternalLink =
      uri.startsWith('http://') || uri.startsWith('https://');
    return (
      <S.StyledLink
        backgroundColor={backgroundColor}
        href={uri}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        target={isExternalLink ? '_blank' : '_self'}
      >
        {children}
      </S.StyledLink>
    );
  },
  [BLOCKS.TABLE]: (node: Block | Inline, children: ReactNode) => {
    return (
      <S.StyledTable>
        <tbody>{children}</tbody>
      </S.StyledTable>
    );
  },
  [BLOCKS.TABLE_CELL]: (node: Block | Inline, children: ReactNode) => {
    return <S.StyledCell>{children}</S.StyledCell>;
  },
});

export type TextProps = {
  backgroundColor?: string | null;
  characterLimit?: number;
  enableShowMore?: boolean;
  isCountDown?: boolean | null;
  showMoreType?: 'button' | 'icon';
  text?:
    | ContentfulCardFragment['cardText']
    | ContentfulCardFragment['cardText2']
    | ContentfulHeroProductListingFragment['heroProductListingText'];
  textColor?: string | null;
  type?: string | null;
};

export const CardRichTextRenderer = ({
  backgroundColor,
  characterLimit = DEFAULT_CONFIG.CHARACTER_LIMIT,
  enableShowMore = false,
  isCountDown,
  showMoreType = 'button',
  text,
  textColor,
  type,
}: TextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const data = useTableData(text?.json.content);

  if (!text) return null;

  const textLength = text.json.content
    .map((node: Block | Inline) =>
      node.content
        ?.map((child) => ('value' in child ? child.value : ''))
        .join(''),
    )
    .join('').length;

  const enableIconShowMore =
    textLength > characterLimit && enableShowMore && showMoreType === 'icon';

  const OPTIONS: Options = {
    renderMark: renderMarkOption,
    renderNode: {
      ...renderNodeOption(backgroundColor ?? undefined),
      [BLOCKS.TABLE_ROW]: (node: Block | Inline, children: ReactNode) =>
        renderTableRow(node, children, data),
      [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
        const textValue = node.content
          ?.map((child) =>
            'value' in child ? (child as unknown as ContentfulText).value : '',
          )
          .join('');

        if (
          textValue?.includes('{{InsertCookieSettings}}') ||
          textValue?.includes('%{{contact.phone}}%') ||
          textValue?.includes('%{{contact.email}}%')
        ) {
          return (
            <InsertDynamicaValuesIntoTextWrapper node={node}>
              {children}
              <ShowMore
                enableShowMore={enableIconShowMore}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                showMoreType={showMoreType}
              />
            </InsertDynamicaValuesIntoTextWrapper>
          );
        }
        return (
          <p>
            {children}
            <ShowMore
              enableShowMore={enableIconShowMore}
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
              showMoreType={showMoreType}
            />
          </p>
        );
      },
    },
    renderText: RENDER_TEXT_OPTION,
  };

  if (textLength <= characterLimit || !enableShowMore) {
    return (
      <S.Text
        $isCountDown={isCountDown}
        color={textColor ?? theme.colors.default}
        type={type}
      >
        {documentToReactComponents(text.json, OPTIONS)}
      </S.Text>
    );
  }

  const displayContent = isExpanded
    ? text.json
    : {
        ...text.json,
        content: text.json.content
          .map((node: Block) => {
            if (node.nodeType !== 'paragraph') return node;

            let currentLength = 0;
            const newContent = [];

            for (const child of node.content) {
              if (!('value' in child)) {
                newContent.push(child);
                continue;
              }

              const remainingChars = characterLimit - currentLength;
              if (currentLength >= characterLimit) break;

              if (currentLength + child.value.length <= characterLimit) {
                newContent.push(child);
                currentLength += child.value.length;
              } else {
                newContent.push({
                  ...child,
                  value: child.value.slice(0, remainingChars) + '...',
                });
                currentLength = characterLimit;
                break;
              }
            }

            return {
              ...node,
              content: newContent,
            };
          })
          .filter((node: Block) => node.content.length > 0),
      };

  return (
    <Flex alignItems='center' flexDirection='column'>
      <S.Text
        $isCountDown={isCountDown}
        color={textColor ?? theme.colors.default}
        type={type}
      >
        {documentToReactComponents(displayContent, OPTIONS)}
      </S.Text>
      <ShowMore
        enableShowMore={enableShowMore && showMoreType === 'button'}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        showMoreType={showMoreType}
      />
    </Flex>
  );
};
